import React, { useEffect, useState } from 'react';
import { Loader2, Save, X, UserPen } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/utils/apiError';
import { HearAboutSelect } from '@/components/training/HearAboutSelect';

/** Staff: edit a trainee's contact details straight from the register. */
export function EditTraineeDetailsDialog({ reg, courseId, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '', hear_about: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (reg) setForm({ name: reg.name || '', email: reg.email_synthetic ? '' : (reg.email || ''), phone: reg.phone || '', notes: reg.booking_details?.notes || reg.notes || '', hear_about: reg.hear_about || '' });
  }, [reg]);

  if (!reg) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (form.name.trim().length < 2) { toast.error('Name is too short'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-courses/${courseId}/registrations/${reg.id}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), notes: form.notes, hear_about: form.hear_about }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      toast.success(`${form.name.trim()} updated`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" data-testid="edit-trainee-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2"><UserPen className="w-5 h-5 text-gray-500" /> Edit trainee details</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="edit-trainee-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div><Label htmlFor="et-name">Full name</Label><Input id="et-name" value={form.name} onChange={set('name')} className="mt-1" data-testid="edit-trainee-name" /></div>
          <div><Label htmlFor="et-email">Email</Label><Input id="et-email" type="email" value={form.email} onChange={set('email')} placeholder="name@example.com" className="mt-1" data-testid="edit-trainee-email" /></div>
          <div>
            <Label htmlFor="et-phone">Mobile</Label>
            <Input id="et-phone" value={form.phone} onChange={set('phone')} placeholder="07…" className="mt-1" data-testid="edit-trainee-phone" />
            <p className="text-[11px] text-gray-500 mt-1">+44 numbers are saved as 07… automatically. Texts go to this number.</p>
          </div>
          <HearAboutSelect id="et-hear-about" testid="edit-trainee-hear-about" value={form.hear_about} onChange={(v) => setForm((f) => ({ ...f, hear_about: v }))} />
          <div><Label htmlFor="et-notes">Notes</Label><Textarea id="et-notes" value={form.notes} onChange={set('notes')} rows={3} className="mt-1 text-sm" data-testid="edit-trainee-notes" /></div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="edit-trainee-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1f4030] hover:bg-[#16302a]" data-testid="edit-trainee-save">{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save</Button>
        </div>
      </div>
    </div>
  );
}
